/**
 * DexieAdapter - Implementación de IDatabaseAdapter para Web (Dexie/IndexedDB)
 *
 * Este adapter abstrae las operaciones específicas de Dexie,
 * permitiendo que los repositorios compartidos funcionen en web.
 */

import { DexieDatabase } from './DexieDatabase';
import {
  IDatabaseAdapter,
  WhereClause,
  OrderByClause,
  TransactionOperation,
  WhereOperator,
  PrimaryKey
} from '../IDatabaseAdapter';

export class DexieAdapter implements IDatabaseAdapter {
  constructor(private readonly db: DexieDatabase) {}

  async insert<T>(table: string, data: T): Promise<void> {
    await (this.db as any)[table].add(data);
  }

  async update<T>(table: string, id: PrimaryKey, data: Partial<T>): Promise<void> {
    await (this.db as any)[table].update(id, data);
  }

  async put<T>(table: string, data: T): Promise<void> {
    await (this.db as any)[table].put(data);
  }

  async delete(table: string, id: PrimaryKey): Promise<void> {
    await (this.db as any)[table].delete(id);
  }

  async get<T>(table: string, id: PrimaryKey): Promise<T | undefined> {
    return await (this.db as any)[table].get(id);
  }

  async getAll<T>(table: string): Promise<T[]> {
    return await (this.db as any)[table].toArray();
  }

  async query<T>(table: string, where?: WhereClause, orderBy?: OrderByClause): Promise<T[]> {
    const tableInstance = (this.db as any)[table];
    const clauses = where ? [where] : [];
    const { collection: seeded, remaining } = this.seedCollection(tableInstance, clauses);
    let collection = seeded;

    for (const clause of remaining) {
      collection = this.applyWhere(collection, clause);
    }

    if (orderBy) {
      try {
        let orderedCollection = tableInstance.orderBy(orderBy.column);
        if (orderBy.direction === 'desc') {
          orderedCollection = orderedCollection.reverse();
        }
        collection = orderedCollection;
      } catch (e) {
        // Si el orderBy falla (índice no existe), filtrar en JS
        if (orderBy.direction === 'desc') {
          collection = collection.reverse();
        }
      }
    }

    return await collection.toArray();
  }

  async queryMultiple<T>(table: string, where: WhereClause[], orderBy?: OrderByClause): Promise<T[]> {
    const tableInstance = (this.db as any)[table];
    const { collection: seeded, remaining } = this.seedCollection(tableInstance, where);
    let collection = seeded;

    for (const clause of remaining) {
      collection = this.applyWhere(collection, clause);
    }

    if (orderBy) {
      try {
        let orderedCollection = tableInstance.orderBy(orderBy.column);
        if (orderBy.direction === 'desc') {
          orderedCollection = orderedCollection.reverse();
        }
        collection = orderedCollection;
      } catch (e) {
        if (orderBy.direction === 'desc') {
          collection = collection.reverse();
        }
      }
    }

    return await collection.toArray();
  }

  /**
   * Set de campos indexados de un store (PK simple + índices de un solo campo).
   * Índices compuestos (keyPath array) se excluyen: no sirven para where(campo).equals().
   */
  private getIndexedFields(tableInstance: any): Set<string> {
    try {
      const schema = tableInstance?.schema;
      const fields = new Set<string>();
      const prim = schema?.primKey;
      if (prim && typeof prim.keyPath === 'string') fields.add(prim.keyPath);
      for (const idx of schema?.indexes ?? []) {
        if (typeof idx.keyPath === 'string') fields.add(idx.keyPath);
      }
      return fields;
    } catch {
      return new Set();
    }
  }

  /**
   * Arranca la colección aprovechando un índice cuando es posible.
   *
   * Si entre las clauses hay un `equals` sobre un campo indexado, inicia la
   * colección con `table.where(field).equals(value)` (usa el índice de Dexie en
   * vez de un scan en memoria) y devuelve esa clause separada para no
   * reaplicarla. Sino, arranca con `toCollection()` (scan) igual que antes.
   *
   * Normalización de booleanos: IndexedDB NO acepta booleanos como clave. Para
   * `is_deleted` (que se guarda como 0/1 vía los hooks de DexieDatabase, y cuya
   * clause llega con `value: false|true` desde los repositorios), convertimos el
   * valor booleano a 0/1 en el `where().equals()`. Así el índice sobre is_deleted
   * SÍ es utilizable.
   */
  private seedCollection(
    tableInstance: any,
    clauses: WhereClause[],
  ): { collection: any; remaining: WhereClause[] } {
    const toKeyValue = (v: unknown): unknown => {
      if (typeof v === 'boolean') return v ? 1 : 0; // IDB no admite booleanos
      return v;
    };
    const isIndexableValue = (v: unknown) =>
      typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean';
    const indexed = this.getIndexedFields(tableInstance);
    const idx = clauses.findIndex(
      c => c.operator === 'equals' && isIndexableValue(c.value) && indexed.has(c.field),
    );
    if (idx >= 0) {
      const seed = clauses[idx];
      const remaining = clauses.slice(0, idx).concat(clauses.slice(idx + 1));
      return {
        collection: tableInstance.where(seed.field).equals(toKeyValue(seed.value)),
        remaining,
      };
    }
    return { collection: tableInstance.toCollection(), remaining: clauses };
  }

  private applyWhere<T>(collection: any, where: WhereClause): any {
    const { field, operator, value } = where;

    switch (operator) {
      case 'equals':
        return collection.filter((item: T) => this.equalsNormalizado((item as any)[field], value));
      case 'between':
        const [min, max] = (value as [unknown, unknown]);
        return collection.filter((item: T) => {
          const val = (item as any)[field];
          return val >= (min as any) && val <= (max as any);
        });
      case 'greaterThan':
        return collection.filter((item: T) => (item as any)[field] > (value as any));
      case 'lessThan':
        return collection.filter((item: T) => (item as any)[field] < (value as any));
      case 'greaterThanOrEqual':
        return collection.filter((item: T) => (item as any)[field] >= (value as any));
      case 'lessThanOrEqual':
        return collection.filter((item: T) => (item as any)[field] <= (value as any));
      case 'startsWith':
        const startsWithValue = String(value).toLowerCase();
        return collection.filter((item: T) => {
          const itemValue = (item as any)[field];
          return itemValue && String(itemValue).toLowerCase().startsWith(startsWithValue);
        });
      case 'includes':
        const includesValue = String(value).toLowerCase();
        return collection.filter((item: T) => {
          const itemValue = (item as any)[field];
          return itemValue && String(itemValue).toLowerCase().includes(includesValue);
        });
      default:
        return collection;
    }
  }

  /**
   * Igualdad tolerante a la normalización boolean→0/1 de `is_deleted`.
   *
   * El reading hook de Dexie (0/1→boolean) NO aplica dentro de los predicados
   * de `Collection.filter()`: cuando otra cláusula indexada sembró la colección
   * (seedCollection) y la cláusula booleana cae en applyWhere, el predicado ve
   * el valor CRUDO guardado (0/1). `0 === false` descartaba TODAS las filas
   * (ej: `getByStockId` devolvía [] → el FIFO del histórico de engorde quedaba
   * sin lotes → "Alimenticios $0"). Se compara en la misma representación que
   * usa el índice (`where('is_deleted').equals(0/1)`): boolean guardado como
   * tal (legacy) matchea directo, 0/1 matchea por valor, y undefined (fuera
   * del índice) no matchea — paridad exacta con la vía indexada.
   */
  private equalsNormalizado(stored: unknown, value: unknown): boolean {
    if (typeof value === 'boolean') {
      if (typeof stored === 'boolean') return stored === value;
      if (typeof stored === 'number') return stored === (value ? 1 : 0);
      return false;
    }
    return stored === value;
  }

  async bulkInsert<T>(table: string, items: T[]): Promise<void> {
    await (this.db as any)[table].bulkAdd(items);
  }

  async bulkUpsert<T>(table: string, items: T[]): Promise<void> {
    await (this.db as any)[table].bulkPut(items);
  }

  async bulkUpdate<T>(table: string, items: Partial<T>[]): Promise<void> {
    // Dexie expone bulkUpdate nativo: merge parcial atómico por PK.
    const dexieTable = (this.db as any)[table];
    const keyPath: string = dexieTable.schema.primKey.keyPath;
    const changes = items
      .filter(item => {
        const rec = item as Record<string, unknown>;
        return rec && rec[keyPath] !== undefined && rec[keyPath] !== null;
      })
      .map(item => {
        const { [keyPath]: key, ...rest } = item as Record<string, unknown>;
        return { key, changes: rest };
      });
    if (changes.length > 0) {
      await dexieTable.bulkUpdate(changes);
    }
  }

  async bulkDelete(table: string, ids: PrimaryKey[]): Promise<void> {
    await (this.db as any)[table].bulkDelete(ids);
  }

  async count(table: string): Promise<number> {
    return await (this.db as any)[table].count();
  }

  async clear(table: string): Promise<void> {
    await (this.db as any)[table].clear();
  }

  async executeTransaction(operations: TransactionOperation[]): Promise<void> {
    // Dexie maneja transacciones automáticamente con bulk operations
    // Para operaciones individuales, usamos una transacción explícita
    const tables = Array.from(new Set(operations.map(op => op.table)));

    await this.db.transaction('rw', (tables as any), async () => {
      for (const op of operations) {
        const tableInstance = (this.db as any)[op.table];

        switch (op.type) {
          case 'insert':
            await tableInstance.add(op.data);
            break;
          case 'update':
            await tableInstance.update(op.id, op.data);
            break;
          case 'delete':
            await tableInstance.delete(op.id);
            break;
        }
      }
    });
  }
}
